import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ArrowLeft, MessageSquare, Calendar, BookOpen,
  ChevronDown, ChevronUp, RotateCcw, Sparkles
} from 'lucide-react';
import {
  GOALS, getProductsForGoal, getProduct,
  getComparisonBySlug, findComparison, COMPARISONS
} from '../data/products';
import ScheduleModal from '../components/ScheduleModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Analytics helper ────────────────────────────────────────────────────────
const track = (eventType, metadata = {}) => {
  try {
    fetch(`${API_URL}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType, source: 'compare', metadata }),
    }).catch(() => {});
  } catch {}
};

// ─── Research Depth badge ────────────────────────────────────────────────────
const DepthBadge = ({ level }) => {
  const styles = {
    Established: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Growing: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    Emerging: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    Early: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[level] || styles.Early}`}>
      {level} Research
    </span>
  );
};

// ─── Shared Nav ──────────────────────────────────────────────────────────────
const Nav = ({ navigate }) => (
  <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 group">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center">
          <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
        </div>
        <span className="font-heading text-xl font-bold text-white">Bryan 2.0</span>
      </Link>
      <nav className="flex items-center gap-3 sm:gap-5">
        <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2 text-sm"><BookOpen className="h-4 w-4" /> Blog</Link>
        <a href="sms:8016518363" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2 text-sm"><MessageSquare className="h-4 w-4" /> <span className="hidden sm:inline">Text Bryan</span></a>
        <Button onClick={() => navigate('/forecast')} size="sm" className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0 text-sm px-5" data-testid="nav-forecast-btn">
          AI Forecast
        </Button>
      </nav>
    </div>
  </header>
);

// ─── Shared Footer ───────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="pt-12 pb-8 border-t border-white/5">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-8">
        <div>
          <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Tools</h4>
          <ul className="space-y-2.5 text-sm text-slate-500">
            <li><Link to="/forecast" className="hover:text-cyan-400 transition-colors">AI Forecast</Link></li>
            <li><Link to="/compare" className="hover:text-cyan-400 transition-colors">Compare Options</Link></li>
            <li><Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Contact</h4>
          <ul className="space-y-2.5 text-sm text-slate-500">
            <li><a href="sms:8016518363" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Text: 801-651-8363</a></li>
            <li><a href="https://calendly.com/bryanm8623/30min" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Calendar className="h-3 w-3" /> Book a Call</a></li>
          </ul>
        </div>
      </div>
      <div className="text-center text-xs text-slate-600 border-t border-white/5 pt-6">
        <p>All information is educational and does not constitute medical advice. Results vary. All programs include licensed medical provider oversight.</p>
      </div>
    </div>
  </footer>
);

// ─── CTA Section ─────────────────────────────────────────────────────────────
const CtaSection = ({ navigate, setShowSchedule }) => (
  <section className="py-14 sm:py-16 border-t border-slate-800/30">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-3">Not sure which is right?</h2>
      <p className="text-slate-400 mb-8 text-base sm:text-lg leading-relaxed">
        The AI Forecast looks at your goals and situation to suggest what might be worth discussing with your provider.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          onClick={() => { track('compare_to_forecast'); navigate('/forecast'); }}
          size="lg"
          className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0 px-8 py-6 text-base"
          data-testid="compare-forecast-cta"
        >
          Help Me Choose <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Button
          onClick={() => { track('compare_talk_to_bryan'); setShowSchedule(true); }}
          variant="outline"
          size="lg"
          className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6 text-base"
          data-testid="compare-schedule-cta"
        >
          <MessageSquare className="mr-2 h-5 w-5" /> Talk to Bryan
        </Button>
      </div>
    </div>
  </section>
);

// ─── Expandable Section ──────────────────────────────────────────────────────
const Expandable = ({ title, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-800/40 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-800/20 transition-colors"
        data-testid={testId}
      >
        <span className="text-sm font-semibold text-slate-300">{title}</span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 text-sm text-slate-400 leading-relaxed">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Product Card (in goal view) ─────────────────────────────────────────────
const ProductCard = ({ product, onSelect, selected }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className={`relative p-5 sm:p-6 rounded-2xl border transition-all cursor-pointer ${
      selected
        ? 'bg-cyan-500/10 border-cyan-500/40 ring-1 ring-cyan-500/20'
        : 'bg-slate-900/40 border-slate-800/50 hover:border-slate-700'
    }`}
    onClick={() => onSelect(product.id)}
    data-testid={`product-card-${product.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-1">{product.name}</h3>
        <p className="text-sm text-cyan-400 font-medium">{product.tagline}</p>
      </div>
      <DepthBadge level={product.researchDepth} />
    </div>
    <p className="text-base text-slate-300 leading-relaxed mb-4">{product.oneLiner}</p>
    <div className="flex flex-wrap gap-2">
      {product.whyPeopleExplore.map(tag => (
        <span key={tag} className="px-2.5 py-1 rounded-full bg-slate-800/60 text-xs text-slate-400 border border-slate-700/30">{tag}</span>
      ))}
    </div>
    {selected && (
      <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    )}
  </motion.div>
);

// ─── Comparison View ─────────────────────────────────────────────────────────
const ComparisonView = ({ leftProduct, rightProduct, comparison, navigate, setShowSchedule, onBack }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogsLoaded, setBlogsLoaded] = useState(false);

  React.useEffect(() => {
    const allSlugs = [...new Set([...(leftProduct.blogSlugs || []), ...(rightProduct.blogSlugs || [])])];
    if (allSlugs.length === 0) { setBlogsLoaded(true); return; }
    fetch(`${API_URL}/api/blog/posts`)
      .then(r => r.json())
      .then(posts => {
        setBlogPosts(posts.filter(p => allSlugs.includes(p.slug)).slice(0, 3));
        setBlogsLoaded(true);
      })
      .catch(() => setBlogsLoaded(true));
  }, [leftProduct, rightProduct]);

  React.useEffect(() => {
    track('comparison_viewed', { left: leftProduct.id, right: rightProduct.id, slug: comparison?.slug });
  }, [leftProduct.id, rightProduct.id, comparison?.slug]);

  const renderProductColumn = (product) => (
    <div className="flex-1 min-w-0">
      <div className="mb-4">
        <h3 className="text-xl sm:text-2xl font-heading font-bold text-white mb-1">{product.name}</h3>
        <p className="text-sm text-cyan-400 font-medium mb-2">{product.tagline}</p>
        <DepthBadge level={product.researchDepth} />
      </div>

      <div className="space-y-5 mt-6">
        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Why people explore it</h4>
          <div className="flex flex-wrap gap-1.5">
            {product.whyPeopleExplore.map(t => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-slate-800/60 text-xs text-slate-300 border border-slate-700/30">{t}</span>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">How it works</h4>
          <p className="text-sm text-slate-300 leading-relaxed">{product.howItWorks}</p>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">What makes it different</h4>
          <p className="text-sm text-slate-300 leading-relaxed">{product.whatMakesItDifferent}</p>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">May be worth discussing if you're interested in</h4>
          <ul className="space-y-1.5">
            {product.worksWellFor.map(w => (
              <li key={w} className="flex items-start gap-2 text-sm text-slate-300">
                <ArrowRight className="h-3.5 w-3.5 text-cyan-500 mt-1 flex-shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>

        {product.glp1Relation && (
          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Relationship to GLP-1 treatment</h4>
            <p className="text-sm text-slate-400 leading-relaxed">{product.glp1Relation}</p>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        <Expandable title="See the Science" testId={`science-${product.id}`}>
          <p>{product.seeTheScience}</p>
        </Expandable>
        <Expandable title="Important Information" testId={`important-${product.id}`}>
          <p>{product.importantInfo}</p>
        </Expandable>
      </div>

      {product.categoryLink && (
        <Link to={product.categoryLink} className="inline-flex items-center gap-2 mt-4 text-sm text-cyan-500 hover:text-cyan-400 transition-colors">
          Learn more about {product.name} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );

  return (
    <div>
      {/* Back button */}
      <div className="max-w-6xl mx-auto px-6 mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors py-2" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to options
        </button>
      </div>

      {/* 30-Second Answer */}
      {comparison && (
        <div className="max-w-6xl mx-auto px-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/40"
            data-testid="thirty-second-answer"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-cyan-400" />
              <h2 className="text-lg sm:text-xl font-heading font-bold text-white">The 30-Second Answer</h2>
            </div>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{comparison.thirtySecondAnswer}</p>
          </motion.div>
        </div>
      )}

      {/* Side-by-side (desktop) / Stacked (mobile) */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-6">
          <div className="flex-1 p-5 sm:p-6 rounded-2xl bg-slate-900/30 border border-slate-800/40">
            {renderProductColumn(leftProduct)}
          </div>
          <div className="hidden lg:flex items-start pt-20">
            <div className="w-px h-full bg-slate-800/60 relative">
              <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-slate-500 text-xs px-2 py-1 rounded-full border border-slate-800/60">vs</span>
            </div>
          </div>
          <div className="lg:hidden flex justify-center py-2">
            <span className="bg-slate-900 text-slate-500 text-sm px-4 py-1.5 rounded-full border border-slate-800/60 font-medium">vs</span>
          </div>
          <div className="flex-1 p-5 sm:p-6 rounded-2xl bg-slate-900/30 border border-slate-800/40">
            {renderProductColumn(rightProduct)}
          </div>
        </div>
      </div>

      {/* Related blog posts */}
      {blogsLoaded && blogPosts.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 mt-12">
          <h3 className="text-lg font-heading font-bold text-white mb-5">Related Articles</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {blogPosts.map(post => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                onClick={() => track('compare_blog_click', { slug: post.slug })}
                className="group p-4 rounded-xl bg-slate-900/50 border border-slate-800/50 hover:border-slate-700 transition-all"
              >
                <h4 className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors mb-2 line-clamp-2">{post.title}</h4>
                <p className="text-xs text-slate-500 line-clamp-2">{post.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Compare Page ───────────────────────────────────────────────────────
const ComparePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('goals'); // goals | products | compare
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);

  const goalData = GOALS.find(g => g.id === selectedGoal);
  const goalProducts = selectedGoal ? getProductsForGoal(selectedGoal) : [];

  const handleGoalSelect = useCallback((goalId) => {
    setSelectedGoal(goalId);
    setSelected([]);
    setStep('products');
    track('compare_goal_selected', { goal: goalId });
  }, []);

  const handleProductToggle = useCallback((productId) => {
    setSelected(prev => {
      if (prev.includes(productId)) return prev.filter(id => id !== productId);
      if (prev.length >= 2) return [prev[1], productId];
      return [...prev, productId];
    });
    track('compare_product_selected', { product: productId });
  }, []);

  const handleCompare = useCallback(() => {
    if (selected.length === 2) {
      setStep('compare');
      track('comparison_started', { left: selected[0], right: selected[1] });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selected]);

  const handleStartOver = useCallback(() => {
    setStep('goals');
    setSelectedGoal(null);
    setSelected([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBackToProducts = useCallback(() => {
    setStep('products');
    setSelected([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const leftProduct = selected.length >= 1 ? getProduct(selected[0]) : null;
  const rightProduct = selected.length >= 2 ? getProduct(selected[1]) : null;
  const comparison = leftProduct && rightProduct ? findComparison(leftProduct.id, rightProduct.id) : null;

  return (
    <div className="min-h-screen bg-[#020817] text-foreground" data-testid="compare-page">
      <Helmet>
        <title>Compare Options | MyWeightForecast</title>
        <meta name="description" content="Explore and compare therapy options to find what fits your health goals. Simple, educational, and designed to help you have a better conversation with your provider." />
      </Helmet>

      <Nav navigate={navigate} />

      {/* Step: Goals */}
      {step === 'goals' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <section className="py-14 sm:py-20">
            <div className="max-w-4xl mx-auto px-6 text-center mb-12">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                What are you working on?
              </h1>
              <p className="text-lg text-slate-400 max-w-lg mx-auto">
                Pick a goal and we'll show you the options people commonly explore.
              </p>
            </div>
            <div className="max-w-4xl mx-auto px-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {GOALS.map((goal, idx) => {
                  const Icon = goal.icon;
                  return (
                    <motion.button
                      key={goal.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      onClick={() => handleGoalSelect(goal.id)}
                      className="flex flex-col items-center gap-3 p-5 sm:p-6 rounded-2xl bg-slate-900/40 border border-slate-800/50 hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all group"
                      data-testid={`goal-${goal.id}`}
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-slate-800/60 flex items-center justify-center group-hover:bg-cyan-500/10 transition-colors">
                        <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-slate-400 group-hover:text-cyan-400 transition-colors" />
                      </div>
                      <span className="text-sm sm:text-base font-medium text-slate-300 group-hover:text-white transition-colors text-center leading-tight">
                        {goal.label}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Popular Comparisons */}
          <section className="pb-14 sm:pb-20">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-lg font-heading font-semibold text-slate-400 text-center mb-6">Popular comparisons</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {COMPARISONS.slice(0, 4).map(c => (
                  <Link
                    key={c.slug}
                    to={`/compare/${c.slug}`}
                    onClick={() => track('compare_popular_click', { slug: c.slug })}
                    className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/30 border border-slate-800/50 hover:border-slate-700 transition-all group"
                    data-testid={`popular-${c.slug}`}
                  >
                    <span className="text-sm text-slate-300 group-hover:text-white transition-colors flex-1">{c.title}</span>
                    <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-cyan-400 transition-colors flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <CtaSection navigate={navigate} setShowSchedule={setShowSchedule} />
        </motion.div>
      )}

      {/* Step: Products for a goal */}
      {step === 'products' && goalData && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <section className="py-10 sm:py-14">
            <div className="max-w-4xl mx-auto px-6">
              {/* Back + Start Over */}
              <div className="flex items-center justify-between mb-8">
                <button onClick={() => { setStep('goals'); setSelectedGoal(null); setSelected([]); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors py-2" data-testid="back-to-goals">
                  <ArrowLeft className="h-4 w-4" /> All goals
                </button>
                <button onClick={handleStartOver} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-300 transition-colors py-2" data-testid="start-over">
                  <RotateCcw className="h-3.5 w-3.5" /> Start over
                </button>
              </div>

              {/* Heading */}
              <div className="mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700/40 text-sm text-slate-400 mb-4">
                  <goalData.icon className="h-4 w-4 text-cyan-400" />
                  {goalData.label}
                </div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2">{goalData.intro}</h2>
                {selected.length === 0 && (
                  <p className="text-base text-slate-400">Tap any two to compare them side by side.</p>
                )}
                {selected.length === 1 && (
                  <p className="text-base text-cyan-400">Good — now pick a second option to compare.</p>
                )}
              </div>

              {/* Product cards */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {goalProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onSelect={handleProductToggle}
                    selected={selected.includes(product.id)}
                  />
                ))}
              </div>

              {/* Compare button */}
              <AnimatePresence>
                {selected.length === 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800/60 sm:static sm:bg-transparent sm:border-0 sm:p-0 sm:backdrop-blur-0"
                  >
                    <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                      <div className="text-sm text-slate-300 hidden sm:block">
                        <span className="font-semibold text-white">{getProduct(selected[0])?.name}</span>
                        <span className="text-slate-500 mx-2">vs</span>
                        <span className="font-semibold text-white">{getProduct(selected[1])?.name}</span>
                      </div>
                      <Button
                        onClick={handleCompare}
                        size="lg"
                        className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white border-0 px-8 py-5 text-base w-full sm:w-auto"
                        data-testid="compare-btn"
                      >
                        Compare These <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Spacer when floating button is present */}
              {selected.length === 2 && <div className="h-20 sm:hidden" />}
            </div>
          </section>

          <CtaSection navigate={navigate} setShowSchedule={setShowSchedule} />
        </motion.div>
      )}

      {/* Step: Comparison */}
      {step === 'compare' && leftProduct && rightProduct && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <section className="py-10 sm:py-14">
            <ComparisonView
              leftProduct={leftProduct}
              rightProduct={rightProduct}
              comparison={comparison}
              navigate={navigate}
              setShowSchedule={setShowSchedule}
              onBack={handleBackToProducts}
            />
          </section>
          <CtaSection navigate={navigate} setShowSchedule={setShowSchedule} />
        </motion.div>
      )}

      <Footer />
      <ScheduleModal isOpen={showSchedule} onClose={() => setShowSchedule(false)} source="compare_page" />
    </div>
  );
};

// ─── Direct Comparison Page (SEO route: /compare/:slug) ──────────────────────
export const DirectComparisonPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [showSchedule, setShowSchedule] = useState(false);

  const comparison = getComparisonBySlug(slug);
  if (!comparison) {
    return (
      <div className="min-h-screen bg-[#020817] text-foreground">
        <Nav navigate={navigate} />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-white mb-4">Comparison not found</h1>
          <p className="text-slate-400 mb-8">We don't have that comparison yet.</p>
          <Button onClick={() => navigate('/compare')} className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white border-0">
            <ArrowLeft className="mr-2 h-4 w-4" /> Explore Options
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const leftProduct = getProduct(comparison.left);
  const rightProduct = getProduct(comparison.right);

  return (
    <div className="min-h-screen bg-[#020817] text-foreground" data-testid={`comparison-${slug}`}>
      <Helmet>
        <title>{comparison.metaTitle}</title>
        <meta name="description" content={comparison.metaDescription} />
        <link rel="canonical" href={`https://myweightforecast.com/compare/${slug}`} />
      </Helmet>

      <Nav navigate={navigate} />

      <section className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-6 mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/compare" className="hover:text-slate-300 transition-colors">Compare</Link>
            <span>/</span>
            <span className="text-slate-300">{comparison.title}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white">{comparison.title}</h1>
        </div>

        <ComparisonView
          leftProduct={leftProduct}
          rightProduct={rightProduct}
          comparison={comparison}
          navigate={navigate}
          setShowSchedule={setShowSchedule}
          onBack={() => navigate('/compare')}
        />
      </section>

      {/* Other comparisons */}
      <section className="py-10 border-t border-slate-800/30">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-lg font-heading font-semibold text-white mb-5">Other popular comparisons</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {COMPARISONS.filter(c => c.slug !== slug).slice(0, 3).map(c => (
              <Link key={c.slug} to={`/compare/${c.slug}`} className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/30 border border-slate-800/50 hover:border-slate-700 transition-all group">
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors flex-1">{c.title}</span>
                <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-cyan-400 transition-colors flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection navigate={navigate} setShowSchedule={setShowSchedule} />
      <Footer />
      <ScheduleModal isOpen={showSchedule} onClose={() => setShowSchedule(false)} source={`compare_${slug}`} />
    </div>
  );
};

export default ComparePage;
